import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '../../utils/formatters';

export const GlassNav = ({ items, className, vertical = false }) => {
  const location = useLocation();

  return (
    <nav className={cn(
      'flex',
      vertical ? 'flex-col space-y-1' : 'flex-row space-x-1',
      className
    )}>
      {items.map((item) => (
        <GlassNavItem
          key={item.path}
          item={item}
          isActive={location.pathname === item.path}
          vertical={vertical}
        />
      ))}
    </nav>
  );
};

const GlassNavItem = ({ item, isActive, vertical }) => {
  const [isHovered, setIsHovered] = useState(false);
  const Icon = item.icon;

  return (
    <NavLink
      to={item.path}
      className={cn(
        'relative flex items-center gap-3 px-4 py-3 rounded-xl',
        'text-sm font-medium transition-all duration-300',
        'hover:text-white',
        isActive ? 'text-white' : 'text-white/60',
        vertical ? 'w-full' : ''
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Active background indicator */}
      {(isActive || isHovered) && (
        <motion.div
          layoutId="nav-indicator"
          className={cn(
            'absolute inset-0 rounded-xl',
            isActive 
              ? 'bg-gradient-to-r from-accent-primary/20 to-accent-secondary/20 border border-white/10' 
              : 'bg-white/5'
          )}
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        />
      )}

      {/* Glow effect for active item */}
      {isActive && (
        <motion.div
          layoutId="nav-glow"
          className="absolute inset-0 rounded-xl bg-accent-primary/10 blur-xl"
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        />
      )}

      {/* Icon */}
      {Icon && (
        <span className={cn(
          'relative z-10 transition-colors',
          isActive ? 'text-accent-primary' : 'text-white/50'
        )}>
          <Icon className="w-5 h-5" />
        </span>
      )}

      {/* Label */}
      <span className="relative z-10">{item.label}</span>

      {/* Badge */}
      {item.badge && (
        <span className={cn(
          'relative z-10 ml-auto px-2 py-0.5 text-xs rounded-full',
          item.badgeColor === 'danger' 
            ? 'bg-red-500/20 text-red-400' 
            : 'bg-accent-primary/20 text-accent-primary'
        )}>
          {item.badge}
        </span>
      )}

      {/* Active indicator dot */}
      {isActive && vertical && (
        <motion.div
          layoutId="nav-dot"
          className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-gradient-to-b from-accent-primary to-accent-secondary rounded-r-full"
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        />
      )}
    </NavLink>
  );
};

export const GlassNavGroup = ({ title, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-4 py-2 text-xs font-medium text-white/40 uppercase tracking-wider hover:text-white/60 transition-colors"
      >
        {title}
        <motion.svg
          animate={{ rotate: isOpen ? 0 : -90 }}
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </motion.svg>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pt-1 space-y-1">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const GlassTabNav = ({ tabs, activeTab, onTabChange, className }) => (
  <div className={cn(
    'flex items-center gap-1 p-1 rounded-xl bg-white/5 border border-white/10',
    className
  )}>
    {tabs.map((tab) => (
      <button
        key={tab.id}
        onClick={() => onTabChange(tab.id)}
        className={cn(
          'relative flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300',
          activeTab === tab.id ? 'text-white' : 'text-white/60 hover:text-white/80'
        )}
      >
        {activeTab === tab.id && (
          <motion.div
            layoutId="tab-indicator"
            className="absolute inset-0 bg-gradient-to-r from-accent-primary/20 to-accent-secondary/20 rounded-lg border border-white/10"
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
          />
        )}
        <span className="relative z-10 flex items-center gap-2">
          {tab.icon && <tab.icon className="w-4 h-4" />}
          {tab.label}
        </span>
      </button>
    ))}
  </div>
);

export const GlassBreadcrumb = ({ items, className }) => (
  <nav className={cn('flex items-center space-x-2 text-sm', className)}>
    {items.map((item, index) => (
      <React.Fragment key={index}>
        {index > 0 && (
          <span className="text-white/30">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </span>
        )}
        {item.href ? (
          <NavLink
            to={item.href}
            className="text-white/60 hover:text-white/90 transition-colors"
          >
            {item.label}
          </NavLink>
        ) : (
          <span className={cn(
            'font-medium',
            index === items.length - 1 ? 'text-white/90' : 'text-white/60'
          )}>
            {item.label}
          </span>
        )}
      </React.Fragment>
    ))}
  </nav>
);

export default GlassNav;
